import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Input from 'reactstrap/lib/Input';
import { get, noop } from 'lodash-es';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { getUserInfoFromProfile } from 'site-modules/shared/components/lead-form/utils/utils';
import { sendMessage } from 'site-modules/shared/utils/private-party/messaging';
/* Components */
import { FieldError } from 'site-modules/shared/components/field-error/field-error';
import { CaramelCta } from 'site-modules/shared/components/financing/caramel/components/caramel-cta/caramel-cta';
import { Link } from 'site-modules/shared/components/link/link';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { ContactSellerModal } from 'site-modules/shared/components/private-party/contact-seller/contact-seller-modal';
import { ContactSellerModalContent } from 'site-modules/shared/components/private-party/contact-seller/contact-seller-modal-content';
import { DismissibleMessage } from 'site-modules/shared/components/private-party/dismissible-message/dismissible-message';
import { asyncValidation } from 'site-modules/shared/components/form-validation/validation-async';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
/* Constants */
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import {
  CREATIVE_ID_CONTACT_FORM,
  CREATIVE_ID_SUMMARY_MESSAGE,
} from 'client/site-modules/shared/constants/private-party/tracking';
import {
  TAB_ASK_QUESTION,
  TAB_MAKE_OFFER,
  MAX_MESSAGE_LENGTH,
  VALIDATION_ERRORS,
  TITLE_ASK_QUESTION,
  TITLE_MAKE_OFFER,
  TAB_LABEL_ASK_QUESTION,
  SUBMIT_LABEL_ASK_QUESTION,
  TRUST_BADGE_TEXT,
  SUBHEAD_ASK_QUESTION,
  SUBHEAD_MAKE_OFFER,
  DEFAULT_MESSAGE,
  PRIVACY_NOTE_TEXT,
  GUIDELINES_LINK_TEXT,
  GUIDELINES_LINK_URL,
  PRIVACY_LINK_TEXT,
  PRIVACY_LINK_URL,
  ERROR_MESSAGE_USER_BLOCKED,
  ERROR_STATUS_TOO_MANY_REQUESTS,
} from './constants';

import './contact-seller.scss';

export const VALIDATORS = {
  firstName: {
    test: value => !!value?.trim() && validation.validateFirstName(value),
    errorText: value => (!value?.trim() ? VALIDATION_ERRORS.REQUIRED : VALIDATION_ERRORS.INVALID_FIRST_NAME),
  },
  lastName: {
    test: value => !!value?.trim() && validation.validateName(value),
    errorText: value => (!value?.trim() ? VALIDATION_ERRORS.REQUIRED : VALIDATION_ERRORS.INVALID_LAST_NAME),
  },
  email: {
    test: validation.validateEmail,
    errorText: value => (!value?.trim() ? VALIDATION_ERRORS.REQUIRED : VALIDATION_ERRORS.INVALID_EMAIL),
  },
  message: {
    test: value => !!value?.trim(),
    errorText: VALIDATION_ERRORS.REQUIRED,
  },
  offerAmount: {
    test: value => !!value?.trim(),
    errorText: VALIDATION_ERRORS.REQUIRED,
  },
};

export function ContactSellerUI({
  sellerLocation = '',
  vehicleTitle = '',
  vehiclePrice = null,
  onSubmitOffer = noop,
  isAskQuestionOnly = false,
  isDrawerEmbedded = false,
  offerDealStatus = '',
  onCloseDrawer = noop,
  profile = {},
  profileIdm = {},
  listingId = '',
  createOrUpdateIdmData,
  validationErrors = {},
  validate,
  fieldRef,
  setFieldInvalid,
}) {
  const {
    firstName: firstNameFromProfile,
    lastName: lastNameFromProfile,
    email: emailFromProfile,
  } = getUserInfoFromProfile(profileIdm);
  const { isAuthenticated } = profile;
  const [activeTab, setActiveTab] = useState(TAB_ASK_QUESTION);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [offerAmount, setOfferAmount] = useState(vehiclePrice ? String(vehiclePrice) : '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  useEffect(() => {
    if (firstNameFromProfile) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate local form state when profile data arrives asynchronously.
      setFirstName(prev => prev || firstNameFromProfile);
    }
    if (lastNameFromProfile) {
      setLastName(prev => prev || lastNameFromProfile);
    }
    if (emailFromProfile) {
      setEmail(prev => prev || emailFromProfile);
    }
  }, [firstNameFromProfile, lastNameFromProfile, emailFromProfile]);

  const isAskQuestion = activeTab === TAB_ASK_QUESTION;
  const showTabToggle = !isAskQuestionOnly;
  const charsRemaining = MAX_MESSAGE_LENGTH - message.length;
  const title = isAskQuestion ? TITLE_ASK_QUESTION : TITLE_MAKE_OFFER;
  const subhead = isAskQuestion ? SUBHEAD_ASK_QUESTION(sellerLocation) : SUBHEAD_MAKE_OFFER(sellerLocation);

  const handleAskQuestionClick = useCallback(() => {
    setActiveTab(TAB_ASK_QUESTION);
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.CONTACT_FORM_TAB_TOGGLE, {
      subactionName: TrackingConstant.SUBACTION_ASK_A_QUESTION_TOGGLE,
      value: 'Ask a Question',
    });
  }, []);
  const handleMakeOfferClick = useCallback(() => {
    setActiveTab(TAB_MAKE_OFFER);
    EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.CONTACT_FORM_TAB_TOGGLE, {
      subactionName: TrackingConstant.SUBACTION_MAKE_AN_OFFER_TOGGLE,
      value: 'Make An Offer',
    });
  }, []);
  const handleFirstNameChange = useCallback(e => setFirstName(e.target.value), []);
  const handleLastNameChange = useCallback(e => setLastName(e.target.value), []);
  const handleEmailChange = useCallback(e => setEmail(e.target.value), []);
  const handleMessageChange = useCallback(e => setMessage(e.target.value), []);
  const handleOfferAmountChange = useCallback(e => setOfferAmount(e.target.value.replace(/\D/g, '')), []);
  const handleBlur = useCallback(e => validate(e, e.target.name), [validate]);
  const firstNameInnerRef = useCallback(el => fieldRef('firstName', el), [fieldRef]);
  const lastNameInnerRef = useCallback(el => fieldRef('lastName', el), [fieldRef]);
  const emailInnerRef = useCallback(el => fieldRef('email', el), [fieldRef]);
  const messageInnerRef = useCallback(el => fieldRef('message', el), [fieldRef]);
  const offerAmountInnerRef = useCallback(el => fieldRef('offerAmount', el), [fieldRef]);
  const handleModalToggle = useCallback(() => setIsModalOpen(prev => !prev), []);
  const handleCloseApiErrorMessage = useCallback(() => setApiErrorMessage(''), []);
  const resetFormFields = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setMessage('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isAskQuestion) {
      EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.SEND_MESSAGE, {
        value: 'Send Message',
      });
    }

    handleCloseApiErrorMessage();
    const isValid = validate();

    if (!isValid) return '';

    if (isAskQuestion) {
      setIsSendingMessage(true);
    }

    const { isEmailValid } = await asyncValidation.fetchEmailValidation({ email });

    if (!isEmailValid) {
      setFieldInvalid('email');
      setIsSendingMessage(false);
      return '';
    }

    if (isAskQuestion) {
      const { conversationId, errorMessage, errorStatus, errorMessageForTracking } = await sendMessage({
        listingId,
        message,
        email,
        firstName,
        isAuthenticated,
        createOrUpdateIdmData,
      });

      if (errorMessage === ERROR_MESSAGE_USER_BLOCKED || errorStatus === ERROR_STATUS_TOO_MANY_REQUESTS) {
        setIsModalOpen(true); // fake success message for blocked user
        resetFormFields();
        setIsSendingMessage(false);
        EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.SEND_MESSAGE_FAILED, {
          value: `${errorStatus} ${errorMessageForTracking}`,
        });
        return '';
      }

      if (conversationId) {
        EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.SEND_MESSAGE_SUCCEEDED, {
          value: conversationId,
        });
        setIsModalOpen(true);
        resetFormFields();
      } else {
        EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.SEND_MESSAGE_FAILED, {
          value: `${errorStatus} ${errorMessageForTracking}`,
        });
        setApiErrorMessage(errorMessage);
      }

      setIsSendingMessage(false);
      return '';
    }

    return onSubmitOffer();
  }, [
    validate,
    isAskQuestion,
    email,
    onSubmitOffer,
    firstName,
    setFieldInvalid,
    listingId,
    message,
    isAuthenticated,
    createOrUpdateIdmData,
    handleCloseApiErrorMessage,
    resetFormFields,
  ]);

  return isModalOpen && isDrawerEmbedded ? (
    <ContactSellerModalContent onToggle={onCloseDrawer} />
  ) : (
    <div
      data-tracking-parent={CREATIVE_ID_CONTACT_FORM}
      className={classnames('contact-seller p-1_5 mt-1_5 pos-r overflow-hidden', isDrawerEmbedded && 'drawer-embedded')}
    >
      {showTabToggle && (
        <div className="contact-seller-tabs bg-cool-gray-70 rounded-20 p-0_25 d-flex mb-1_25" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={isAskQuestion}
            className={classnames(
              'contact-seller-tab flex-fill d-flex align-items-center justify-content-center border-0 rounded-20 px-1 py-0_5 size-14 text-center c-pointer',
              {
                'bg-white fw-medium text-cool-gray-30 contact-seller-tab-active': isAskQuestion,
                'bg-transparent text-cool-gray-40': !isAskQuestion,
              }
            )}
            onClick={handleAskQuestionClick}
          >
            {isAskQuestion && <i className="icon-checkmark text-blue-50 me-0_25 xsmall" aria-hidden />}
            {TAB_LABEL_ASK_QUESTION}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isAskQuestion}
            className={classnames(
              'contact-seller-tab flex-fill d-flex align-items-center justify-content-center border-0 rounded-20 px-1 py-0_5 size-14 text-center c-pointer',
              {
                'bg-white fw-medium text-cool-gray-30 contact-seller-tab-active': !isAskQuestion,
                'bg-transparent text-cool-gray-40': isAskQuestion,
              }
            )}
            onClick={handleMakeOfferClick}
          >
            {!isAskQuestion && <i className="icon-checkmark text-blue-50 me-0_25 xsmall" aria-hidden />}
            {TITLE_MAKE_OFFER}
          </button>
        </div>
      )}

      <div className="mb-1_25">
        <h3 className="size-24 fw-bold mb-0_5">{title}</h3>
        <p className="size-16 text-cool-gray-30 mb-0">{subhead}</p>
      </div>

      <div className="d-flex flex-column gap-1">
        <div className="d-flex gap-1">
          <div className="flex-fill">
            <div
              className={classnames('contact-seller-field bg-white rounded-8 px-1 py-0_5', {
                'contact-seller-field-invalid': validationErrors.firstName,
              })}
            >
              <label className="size-12 text-cool-gray-40 mb-0 d-block" htmlFor="contact-seller-first-name">
                First Name
              </label>
              <Input
                id="contact-seller-first-name"
                name="firstName"
                type="text"
                className="contact-seller-input border-0 p-0 size-16 text-cool-gray-30"
                value={firstName}
                onChange={handleFirstNameChange}
                onBlur={handleBlur}
                innerRef={firstNameInnerRef}
                aria-label="First Name"
                aria-describedby="contact-seller-first-name-error"
              />
            </div>
            <FieldError
              id="contact-seller-first-name-error"
              error={validationErrors.firstName}
              classes="mt-0_25 mb-0"
              isLiveRegion
            />
          </div>
          <div className="flex-fill">
            <div
              className={classnames('contact-seller-field bg-white rounded-8 px-1 py-0_5', {
                'contact-seller-field-invalid': validationErrors.lastName,
              })}
            >
              <label className="size-12 text-cool-gray-40 mb-0 d-block" htmlFor="contact-seller-last-name">
                Last Name
              </label>
              <Input
                id="contact-seller-last-name"
                name="lastName"
                type="text"
                className="contact-seller-input border-0 p-0 size-16 text-cool-gray-30"
                value={lastName}
                onChange={handleLastNameChange}
                onBlur={handleBlur}
                innerRef={lastNameInnerRef}
                aria-label="Last Name"
                aria-describedby="contact-seller-last-name-error"
              />
            </div>
            <FieldError
              id="contact-seller-last-name-error"
              error={validationErrors.lastName}
              classes="mt-0_25 mb-0"
              isLiveRegion
            />
          </div>
        </div>

        <div>
          <div
            className={classnames('contact-seller-field bg-white rounded-8 px-1 py-0_5', {
              'contact-seller-field-invalid': validationErrors.email,
            })}
          >
            <label className="size-12 text-cool-gray-40 mb-0 d-block" htmlFor="contact-seller-email">
              Email Address
            </label>
            <Input
              id="contact-seller-email"
              name="email"
              type="email"
              className="contact-seller-input border-0 p-0 size-16 text-cool-gray-30"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleBlur}
              innerRef={emailInnerRef}
              aria-label="Email Address"
              aria-describedby="contact-seller-email-error"
            />
          </div>
          <FieldError
            id="contact-seller-email-error"
            error={validationErrors.email}
            classes="mt-0_25 mb-0"
            isLiveRegion
          />
        </div>

        {isAskQuestion && (
          <div>
            <div
              className={classnames(
                'contact-seller-field contact-seller-message-field bg-white rounded-8 px-1 py-0_5',
                {
                  'contact-seller-field-invalid': validationErrors.message,
                }
              )}
            >
              <label className="size-12 text-cool-gray-40 mb-0 d-block" htmlFor="contact-seller-message">
                Message
              </label>
              <Input
                id="contact-seller-message"
                name="message"
                type="textarea"
                className="contact-seller-input border-0 p-0 size-16 text-cool-gray-30"
                value={message}
                onChange={handleMessageChange}
                onBlur={handleBlur}
                innerRef={messageInnerRef}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder={DEFAULT_MESSAGE(vehicleTitle, formatPriceStringV2(vehiclePrice))}
                rows={4}
                aria-label="Message"
                aria-describedby="contact-seller-message-error"
              />
            </div>
            <FieldError
              id="contact-seller-message-error"
              error={validationErrors.message}
              classes="mt-0_25 mb-0"
              isLiveRegion
            />
            <p className="size-12 text-cool-gray-30 mt-0_25 mb-0">{charsRemaining} characters remaining</p>
          </div>
        )}

        {!isAskQuestion && (
          <div>
            <div
              className={classnames('contact-seller-field bg-white rounded-8 px-1 py-0_5', {
                'contact-seller-field-invalid': validationErrors.offerAmount,
              })}
            >
              <label className="size-12 text-cool-gray-40 mb-0 d-block" htmlFor="contact-seller-offer">
                Offer Amount
              </label>
              <div className="d-flex align-items-center gap-0_5">
                <span className="size-16 text-cool-gray-30">$</span>
                <Input
                  id="contact-seller-offer"
                  name="offerAmount"
                  type="text"
                  className="contact-seller-input border-0 p-0 size-16 text-cool-gray-30"
                  value={offerAmount ? Number(offerAmount).toLocaleString('en-US') : ''}
                  onChange={handleOfferAmountChange}
                  onBlur={handleBlur}
                  innerRef={offerAmountInnerRef}
                  aria-label="Offer Amount in dollars"
                  aria-describedby="contact-seller-offer-error"
                />
              </div>
            </div>
            <FieldError
              id="contact-seller-offer-error"
              error={validationErrors.offerAmount}
              classes="mt-0_25 mb-0"
              isLiveRegion
            />
          </div>
        )}
      </div>

      <div className="mt-1_25">
        {!isAskQuestion && <p className="size-14 fw-medium text-cool-gray-30 text-center mb-1">{TRUST_BADGE_TEXT}</p>}

        <div>
          {isAskQuestion ? (
            <>
              <Button
                color="blue-50"
                className="contact-seller-submit w-100 fw-medium size-16 text-transform-none"
                onClick={handleSubmit}
                disabled={isSendingMessage}
              >
                {isSendingMessage && (
                  <span className="pe-0_5" data-testid="spinner">
                    <Spinner size={14} thickness={1} color="white" />
                  </span>
                )}
                {SUBMIT_LABEL_ASK_QUESTION}
              </Button>
              {apiErrorMessage && (
                <DismissibleMessage
                  containerClassName="mt-1 p-1 border border-danger bg-red-100"
                  onClose={handleCloseApiErrorMessage}
                >
                  <i className="icon-warning2 size-20 text-red-50 align-self-center" aria-hidden />
                  <span className="text-sm text-cool-gray-30">{apiErrorMessage}</span>
                </DismissibleMessage>
              )}
            </>
          ) : (
            <CaramelCta
              isPrivateListing
              handleCaramelMakeOffer={handleSubmit}
              dealStatus={offerDealStatus}
              containerClassName="mt-0"
              buttonClassName="fw-medium size-16 text-white"
              customStyles={{ background: '#2070e8', borderRadius: 'none', padding: '10px 0' }}
            />
          )}
        </div>

        <div
          className="d-flex align-items-start gap-0_5 mt-1"
          data-tracking-parent={CREATIVE_ID_SUMMARY_MESSAGE}
          data-no-widget-view
        >
          <span className="icon-shield-check size-24 text-cool-gray-50 flex-shrink-0" aria-hidden="true" />
          <p className="size-12 text-cool-gray-30 mb-0">
            {PRIVACY_NOTE_TEXT}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={GUIDELINES_LINK_URL}
              className="text-gray-darker"
              data-tracking-id="view_fsbo_guidelines"
              data-tracking-value={GUIDELINES_LINK_TEXT}
            >
              <u>{GUIDELINES_LINK_TEXT}</u>
            </Link>
            {'. '}
            <Link target="_blank" rel="noopener noreferrer" to={PRIVACY_LINK_URL} className="text-gray-darker">
              <u>{PRIVACY_LINK_TEXT}</u>
            </Link>
            {'.'}
          </p>
        </div>
      </div>

      <ContactSellerModal isOpen={isModalOpen && !isDrawerEmbedded} onToggle={handleModalToggle}>
        <ContactSellerModalContent onToggle={handleModalToggle} />
      </ContactSellerModal>
    </div>
  );
}

ContactSellerUI.propTypes = {
  sellerLocation: PropTypes.string,
  vehicleTitle: PropTypes.string,
  vehiclePrice: PropTypes.number,
  onSubmitOffer: PropTypes.func,
  isAskQuestionOnly: PropTypes.bool,
  offerDealStatus: PropTypes.string,
  isDrawerEmbedded: PropTypes.bool,
  onCloseDrawer: PropTypes.func,
  profileIdm: PropTypes.shape({}),
  profile: PropTypes.shape({ isAuthenticated: PropTypes.bool }),
  listingId: PropTypes.string,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    message: PropTypes.string,
    offerAmount: PropTypes.string,
  }),
  validate: PropTypes.func.isRequired,
  fieldRef: PropTypes.func.isRequired,
  setFieldInvalid: PropTypes.func.isRequired,
};

export const mapStateToProps = state => ({
  profileIdm: get(state, 'profile.data.idm'),
  profile: get(state, 'profile'),
});
export const ContactSeller = connect(mapStateToProps)(insiderMethods(formValidation(ContactSellerUI, VALIDATORS)));
